import { Planet } from '@apeiron/planet'
import { INFO_POPUP_TYPE, InfoPopup } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import { CoreAboutSection } from '@src/components/planet/detail/planetAboutSection/CoreAboutSection'
import { PlanetAboutSection } from '@src/components/planet/detail/planetAboutSection/PlanetAboutSection'
import { PlanetDetailCard } from '@src/components/planet/detail/PlanetDetailCard'
import { PlanetElementSection } from '@src/components/planet/detail/planetElementSection/PlanetElementSection'
import PlanetSkillMainSection from '@src/components/planet/detail/planetSkillsSection/PlanetSkillMainSection'
import PlanetStats from '@src/components/planet/detail/PlanetStats'
import ApeironSectionTitle from '@src/components/share/apeiron/ApeironSectionTitle'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import { isCore } from '@src/util/apeiron/planet'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  border-radius: 20px;
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 80%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
  }
  height: 90%;
  max-width: 1200px;
  max-height: 1000px;
  .Custom-Content {
    height: 100%;
  }
`

const StyledPlanetDetailCard = styled(PlanetDetailCard)`
  ${props => props.theme.breakpoints.up('res1280')} {
    margin-left: auto;
  }
`

const Container = styled(Box)`
  display: flex;
  justify-content: space-between;
  height: 100%;
  ${props => props.theme.breakpoints.up('res1280')} {
    flex-direction: row;
    align-items: flex-start;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    flex-direction: column;
    overflow-y: auto;
    align-items: center;
  }
`

const InfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 0;

  padding: 0 40px 40px 40px;
  ${props => props.theme.breakpoints.up('res1280')} {
    overflow-y: auto;
    width: 65%;
    height: 100%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
    padding: 20px;
    margin-bottom: 40px;
  }
`

const SeasonAvatarPopup: FC<Props> = (props: Props) => {
  const { open, onClose, planet } = props

  const { t } = useTranslation()

  const { getPlanetTypeData } = usePlanetTypeContext()

  const planetType = getPlanetTypeData(planet.planetType)

  return (
    <Popup open={open} type={INFO_POPUP_TYPE.BLACK} onClose={onClose}>
      <Container>
        <StyledPlanetDetailCard
          isBookmarked={false}
          isEditable={false}
          planet={planet}
          onNameUpdate={() => {}}
        />
        <InfoContainer>
          <ApeironSectionTitle title={t('planet.detail.about.title')} />
          {isCore(planet) ? (
            <CoreAboutSection planet={planet} />
          ) : (
            <PlanetAboutSection planet={planet} />
          )}

          <ApeironSectionTitle title={t('planet.detail.element.title')} />
          <PlanetElementSection
            planet={planet}
            src={planetType ? planetType.planetTypeImage : undefined}
          />

          {!isCore(planet) && (
            <>
              {featureAllowed(FeatureFlagType.ShowPlanetStat) && (
                <>
                  <ApeironSectionTitle title={t('planet.detail.stats.title')} />
                  <PlanetStats planet={planet} />
                </>
              )}
              <PlanetSkillMainSection planet={planet} />
            </>
          )}
        </InfoContainer>
      </Container>
    </Popup>
  )
}

type Props = {
  planet: Planet
  open: boolean
  onClose: () => void
}

export default SeasonAvatarPopup
